/**
SELLERIE MARINE - SERVICE API PRINCIPAL

📋 DESCRIPTION:
Service central pour toutes les communications avec l'API backend.
Gestion de l'authentification, des appels API et de l'optimisation des calculs de prix.

🎯 RESPONSABILITÉS:
- Gestion centralisée des appels API vers le backend Flask
- Authentification JWT automatique avec gestion des en-têtes
- Calculs de prix avec optimisation calepinage intégrée
- Cache intelligent des matières et données utilisateur
- Intégration des services métier (entreprise, paramètres, calepinage)
- Gestion des erreurs avec fallbacks automatiques

🔗 DÉPENDANCES:
- types.ts: Définitions TypeScript des interfaces métier
- EntrepriseService: Informations entreprise pour calculs TVA
- ParametreService: Tarifs et coefficients utilisateur
- CalepinageService: Optimisation frontend des matières
- DevisService: Gestion complète des devis
- SubscriptionService: Gestion Premium/Standard

📡 INTERACTIONS:
- Backend: Tous les endpoints API via fetch() sécurisé
- Frontend: Utilisé par tous les composants nécessitant des données
- LocalStorage: Cache des données utilisateur et matières
- Services: Orchestration des autres services frontend

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Gestion automatique de l'authentification JWT
- Cache intelligent pour optimiser les performances
- Support Premium/Standard avec fonctionnalités différenciées
- Intégration complète du calepinage optimisé (15-25% d'économies)
- Calculs adaptatifs selon statut TVA entreprise
*/
// Service pour communiquer avec l'API backend

import { Matiere, Article, CalculPrix } from '../pages/Devis/ConfigurateurArticle/types';
import { Devis } from './types';
import AuthContext from '../contexts/AuthContext';
import { useContext } from 'react';
import { entrepriseService } from './EntrepriseService';
import { parametreService, TarifCoefficient } from './ParametreService';
import { subscriptionService } from './SubscriptionService';
import { CalepinageService } from './CalepinageService';
import { DimensionsService } from './DimensionsService';
import { DevisService } from './DevisService';
import { GroupeMatiere } from '../types/dimensions';
import { FallbackAlerts } from './FallbackNotificationService';
import { getApiBaseUrl } from '../config/api';
import { apiFetch } from './httpClient';

// URL de base de l'API
const API_BASE_URL = getApiBaseUrl();

/**
 * Classe pour gérer les appels API
 */
class ApiService {
  /**
   * Retourne l'URL de base de l'API
   */
  getBaseUrl(): string {
    return API_BASE_URL;
  }

  /**
   * Récupère le token d'authentification depuis le localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Récupère l'ID de l'utilisateur depuis le localStorage
   */
  private getUserId(): string | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.userId;
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'ID utilisateur:', error);
      }
    }
    return null;
  }

  /**
   * Crée les en-têtes pour les requêtes API avec authentification
   */
  createHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }
  
  /**
   * Récupère la liste des matières depuis l'API
   */
  async getMatieres(): Promise<Matiere[]> {
    
    // Utiliser directement l'URL de l'API
    const appUrl = API_BASE_URL;
    
    try {
      const data = await apiFetch<Matiere[]>('/matieres', { method: 'GET', cache: 'no-store' });
      localStorage.setItem('matieres', JSON.stringify(data));
      return data;
    } catch (error) {
      console.error("Erreur lors de la récupération des matières:", error);
      
      // 🚨 NOTIFICATION FALLBACK - Matières serveur indisponibles
      FallbackAlerts.matieres('Récupération matières depuis API');
      
      // En cas d'erreur, essayer de récupérer les matières depuis le localStorage
      const matieresStr = localStorage.getItem('matieres');
      if (matieresStr) {
        try {
          const matieres = JSON.parse(matieresStr);

          return matieres;
        } catch (parseError) {
          console.error("Erreur lors de la récupération des matières depuis le localStorage:", parseError);
        }
      }
      
      // Si tout échoue, retourner une liste vide
      return [];
    }
  }

  /**
   * Récupère un article par son ID
   */
  async getArticle(id: string): Promise<Article> {
    const response = await fetch(`${API_BASE_URL}/articles/${id}`, {
      headers: this.createHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération de l'article ${id}`);
    }
    
    return await response.json();
  }

  /**
   * Authentifie l'utilisateur avec des identifiants par défaut
   * Cette méthode est utilisée uniquement pour le calcul de prix en mode démo
   */
  private async authenticateForDemo(): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: 'demo@example.com',
          password: 'password'
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Erreur lors de l'authentification: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Sauvegarder le token dans le localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return data.token;
    } catch (error) {
      console.error("Erreur lors de l'authentification pour le calcul de prix:", error);
      return '';
    }
  }

  /**
   * Calcule le prix d'un article via l'API
   */
  async calculerPrix(article: Article): Promise<CalculPrix> {
    try {
      // Appel au backend pour obtenir le prix de base et les détails
      const resultatBackend = await this.appelBackendCalculPrix(article);
      
      // Récupérer les matières pour l'optimisation
      const matieres = await this.getMatieres();
      
      // S'assurer que l'article a un ID temporaire et un prix unitaire temporaire pour le calcul
      const articleAvecId = {
        ...article,
        id: article.id || `temp-article-${Math.random().toString(36).substring(2, 8)}`,
        prixUnitaire: article.prixUnitaire || 1 // Utiliser un prix unitaire temporaire si non défini
      };
      
      // Créer un devis temporaire avec cet article pour utiliser DevisService.calculerPrixDevis
      const devisTemp = {
        id: 'temp',
        reference: 'temp',
        client: 'temp',
        date: new Date().toISOString(),
        articles: [articleAvecId],
        groupesMatiere: [],
        coutMatiereTotal: 0,
        coutMainOeuvreTotal: 0,
        coutAccessoiresTotal: 0,
        coutPetitesFournitures: 0,
        montantTotal: 0
      };
      
      // Utiliser DevisService pour calculer le prix avec calepinage optimisé
      const devisOptimise = await DevisService.calculerPrixDevis(devisTemp, matieres);
      
      // Récupérer l'article optimisé
      const articleOptimise = devisOptimise.articles[0];
      
        // Mettre à jour le résultat du backend avec les informations de calepinage
        if (articleOptimise.prix !== undefined && articleOptimise.couts) {
          resultatBackend.prix = articleOptimise.prix;
          
          // Ne pas Ã©craser les coÃ»ts calculÃ©s par le backend
        }
      
      // Mettre à jour les informations de calepinage
      if (resultatBackend.details && devisOptimise.groupesMatiere.length > 0) {
        // S'assurer que calepinage existe
        if (!resultatBackend.details.calepinage) {
          resultatBackend.details.calepinage = {
            utilisationCalepinage: false,
            matieresEnStock: {
              dessus: article.materiaux.dessus.enStock === true,
              dessous: article.materiaux.dessous.enStock === true,
              cotes: article.materiaux.cotes.enStock === true
            },
            majorationStandard: !subscriptionService.hasPremiumFeatures()
          };
        }
        
        // Mettre à jour les informations de calepinage
        resultatBackend.details.calepinage.utilisationCalepinage = 
          !article.materiaux.dessus.enStock || 
          !article.materiaux.dessous.enStock || 
          !article.materiaux.cotes.enStock;
        
        // Ne pas écraser les informations de calepinage si elles sont déjà présentes dans la réponse du backend
        if (!resultatBackend.details.calepinage.matieres || resultatBackend.details.calepinage.matieres.length === 0) {

          // Ajouter les informations des groupes de matière
          resultatBackend.details.calepinage.matieres = devisOptimise.groupesMatiere.map(groupe => {
            if (!groupe.calepinage) return null;
            
            // Calculer la longueur en mètres
            const longueurTotale = groupe.calepinage.longueurTotale ?? 0;
            const longueurMetres = longueurTotale / 100; // Convertir en mètres
            
            return {
              id: groupe.matiere,
              couleur: groupe.couleur,
              enStock: false, // Par défaut, pas en stock
              longueurTotale,
              longueurMetres,
              faces: groupe.pieces.map(p => p.type)
            };
          }).filter(Boolean) as Array<{
            id: string;
            couleur: string;
            enStock: boolean;
            longueurTotale: number;
            longueurMetres: number;
            faces: string[];
          }>;
          
          // Mettre à jour les longueurs de matière par face
          const longueursMatieres = {
            dessus: 0,
            dessous: 0,
            cotes: 0
          };
          
          // Parcourir les groupes pour récupérer les longueurs par face
          for (const groupe of devisOptimise.groupesMatiere) {
            if (groupe.calepinage) {
              for (const piece of groupe.pieces) {
                if (piece.type === 'dessus') {
                  longueursMatieres.dessus = groupe.calepinage.longueurTotale;
                } else if (piece.type === 'fond') {
                  longueursMatieres.dessous = groupe.calepinage.longueurTotale;
                } else if (piece.type === 'bandeau' || piece.type === 'joue1' || piece.type === 'joue2' || piece.type === 'cylindre') {
                  longueursMatieres.cotes = groupe.calepinage.longueurTotale;
                }
              }
            }
          }
          
          resultatBackend.details.calepinage.longueursMatieres = longueursMatieres;
        } else {
        }
        
        // Marquer quelles matières sont en stock (toujours nécessaire)
        if (resultatBackend.details.calepinage.matieres) {
          for (const matiere of resultatBackend.details.calepinage.matieres) {
            if (matiere && matiere.id) {
              // Vérifier si cette matière correspond à une des matières de l'article
              if (article.materiaux.dessus.matiere === matiere.id) {
                matiere.enStock = article.materiaux.dessus.enStock === true;
              } else if (article.materiaux.dessous.matiere === matiere.id) {
                matiere.enStock = article.materiaux.dessous.enStock === true;
              } else if (article.materiaux.cotes.matiere === matiere.id) {
                matiere.enStock = article.materiaux.cotes.enStock === true;
              }
            }
          }
        }
      }
      
      return resultatBackend;
    } catch (error) {
      console.error("Erreur lors du calcul du prix:", error);
      
      // 🚨 NOTIFICATION FALLBACK CRITIQUE - Calcul prix serveur
      FallbackAlerts.calculPrix('Calcul prix article via API backend');
      
      throw error; // Propager l'erreur pour qu'elle soit gérée par l'appelant
    }
  }

  /**
   * Appel au backend pour calculer le prix de base
   */
  private async appelBackendCalculPrix(article: Article): Promise<CalculPrix> {
    try {
      // Récupérer les informations de l'entreprise pour déterminer le statut TVA
      const entreprise = await entrepriseService.getEntreprise() as any;
      const soumisATVA = entreprise.soumisATVA !== undefined ? entreprise.soumisATVA : !!entreprise.tva;
      const tauxTVA = entreprise.tauxTVA || 20; // Taux par défaut : 20%
      
      // Vérifier si l'utilisateur a un abonnement Premium
      const isPremium = subscriptionService.hasPremiumFeatures();
      
      // Créer une copie de l'article pour éviter de modifier l'original
      const articlePourBackend = { ...article };
      
      // Ne plus mapper les formes - le backend doit supporter toutes les formes directement
      // Chaque forme doit être traitée selon ses spécificités
      
      
      let resultat;
      
      // Créer des en-têtes personnalisés sans authentification pour cette route spécifique
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Ajouter l'ID utilisateur s'il est disponible
      
      
      resultat = await apiFetch<CalculPrix>('/articles/calculer-prix', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          ...articlePourBackend,
          isPremium, // Ajouter l'information sur l'abonnement Premium
          soumisATVA, // Ajouter le statut TVA
          tauxTVA     // Ajouter le taux de TVA
        }),
        credentials: 'include' // Inclure les cookies pour l'authentification
      });
      
      // Ajouter les informations de TVA à la réponse
      resultat.soumisATVA = soumisATVA;
      resultat.tauxTVA = tauxTVA;
      
      // Ajuster le prix en fonction du statut TVA de l'entreprise
      if (soumisATVA) {
        // Si l'entreprise est soumise à TVA, convertir le prix TTC en HT
        // Sauvegarder le prix TTC original
        resultat.prixTTC = resultat.prix;
        
        // Convertir le prix en HT
        resultat.prix = resultat.prix / (1 + tauxTVA / 100);
        
        // Ajuster également les coûts dans les détails
        // Ne pas ajuster les coûts calculés par le backend
      } else {
        // Si l'entreprise n'est pas soumise à TVA, le prix est déjà net
        resultat.prixNet = resultat.prix;
      }
      
      return resultat;
    } catch (error) {
      console.error("Exception lors du calcul du prix:", error);
      throw error;
    }
  }

  /**
   * Optimise le calepinage d'un article en utilisant CalepinageService
   */
  private async optimiserCalepinageArticle(article: Article, resultatBackend: CalculPrix): Promise<CalculPrix> {
    try {
      // Récupérer les matières pour l'optimisation
      const matieres = await this.getMatieres();
      
      // Convertir l'article en pièces pour le calepinage
      // Utiliser un cast pour résoudre l'incompatibilité de types
      const articleDimensions = article as unknown as import('../types/dimensions').Article;
      
      // Regrouper les pièces par matière et couleur
      const groupesMatiere = CalepinageService.regrouperParMatiere([articleDimensions], matieres);
      
      // Optimiser le calepinage pour chaque groupe
      CalepinageService.optimiserTousLesGroupes(groupesMatiere, matieres);
      
      let coutMatieresOptimise = 0;
      let coutMatieresOriginal = 0;
      
      // S'assurer que resultatBackend.details existe
      if (!resultatBackend.details) {
        resultatBackend.details = {
          surfaces: { dessus: 0, fond: 0, bandeau: 0, total: 0 },
          coutures: { dessus: 0, fond: 0, jonction: 0, total: 0 },
          temps: { couture: 0, total_heures: 0 },
          couts: { main_oeuvre: 0, matieres: 0, accessoires: 0, total: 0 },
          dimensions: {
            talon: article.dimensions.talon || 1,
            details: {
              dessus: {},
              fond: {},
              bandeau: {}
            }
          },
          accessoires: {}
        };
      }
      
      // S'assurer que resultatBackend.details.calepinage existe avec la structure correcte
      if (!resultatBackend.details.calepinage) {
        resultatBackend.details.calepinage = {
          utilisationCalepinage: false,
          matieresEnStock: {
            dessus: article.materiaux.dessus.enStock === true,
            dessous: article.materiaux.dessous.enStock === true,
            cotes: article.materiaux.cotes.enStock === true
          },
          majorationStandard: !subscriptionService.hasPremiumFeatures(),
          longueursMatieres: { dessus: 0, dessous: 0, cotes: 0 },
          matieres: []
        };
      }
      
      // Calculer les coûts et économies pour chaque groupe
      const matieresInfo = groupesMatiere.map(groupe => {
        // Si le groupe n'a pas de calepinage, ignorer
        if (!groupe.calepinage) return null;
        
        // Calculer le coût de matière pour ce groupe
        const coutOriginal = groupe.prixMetreLineaire * groupe.pieces.reduce((acc, p) => 
          acc + (p.width <= groupe.laize ? p.height : p.height * (p.width / groupe.laize)), 0) / 100;
        
        const coutOptimise = groupe.prixMetreLineaire * groupe.calepinage.longueurTotale / 100;
        
        // Ajouter au coût total
        coutMatieresOriginal += coutOriginal;
        coutMatieresOptimise += coutOptimise;
        
        return {
          id: groupe.matiere,
          couleur: groupe.couleur,
          enStock: false, // Par défaut, pas en stock
          longueurTotale: groupe.calepinage.longueurTotale,
          faces: groupe.pieces.map(p => p.type),
          economie: groupe.pourcentageEconomie || 0,
          coutOriginal,
          coutOptimise
        };
      }).filter(Boolean) as Array<{
        id: string;
        couleur: string;
        enStock: boolean;
        longueurTotale: number;
        faces: string[];
        economie: number;
        coutOriginal: number;
        coutOptimise: number;
      }>;
      
      // Mettre à jour les informations de calepinage
      if (resultatBackend.details.calepinage) {
        // Ajouter les matières au calepinage
        resultatBackend.details.calepinage.matieres = matieresInfo;
        
        // Mettre à jour les longueurs de matière par face
        const longueursMatieres = {
          dessus: 0,
          dessous: 0,
          cotes: 0
        };
        
        // Parcourir les groupes pour récupérer les longueurs par face
        for (const matiere of matieresInfo) {
          for (const face of matiere.faces) {
            if (face === 'dessus') {
              longueursMatieres.dessus = matiere.longueurTotale;
            } else if (face === 'fond') {
              longueursMatieres.dessous = matiere.longueurTotale;
            } else if (face === 'bandeau' || face === 'joue1' || face === 'joue2' || face === 'cylindre') {
              longueursMatieres.cotes = matiere.longueurTotale;
            }
          }
        }
        
        resultatBackend.details.calepinage.longueursMatieres = longueursMatieres;
        
        // Marquer quelles matières sont en stock
        if (article.materiaux) {
          for (const matiere of matieresInfo) {
            // Vérifier si cette matière correspond à une des matières de l'article
            if (article.materiaux.dessus.matiere === matiere.id) {
              matiere.enStock = article.materiaux.dessus.enStock === true;
            } else if (article.materiaux.dessous.matiere === matiere.id) {
              matiere.enStock = article.materiaux.dessous.enStock === true;
            } else if (article.materiaux.cotes.matiere === matiere.id) {
              matiere.enStock = article.materiaux.cotes.enStock === true;
            }
          }
        }
        
        // Vérifier si le calepinage a été utilisé pour au moins une matière
        resultatBackend.details.calepinage.utilisationCalepinage = false;
        if (article.materiaux) {
          if (!article.materiaux.dessus.enStock || !article.materiaux.dessous.enStock || !article.materiaux.cotes.enStock) {
            resultatBackend.details.calepinage.utilisationCalepinage = true;
          }
        }
      }
      
      // Ajuster le prix final si nécessaire
      if (resultatBackend.details.calepinage?.utilisationCalepinage && resultatBackend.details.couts) {
        // Remplacer le coût des matières par le coût optimisé
        resultatBackend.details.couts.matieres = coutMatieresOptimise;
        // Recalculer le total (conserver la valeur backend)

        
        // Ne pas appliquer de marge supplémentaire - la marge est déjà incluse dans le taux horaire
        // et les remises fournisseurs sur les matières
        resultatBackend.prix = resultatBackend.details.couts.total;
      }
      
      return resultatBackend;
    } catch (error) {
      console.error("Erreur lors de l'optimisation du calepinage:", error);
      // En cas d'erreur, retourner le résultat initial sans optimisation
      return resultatBackend;
    }
  }

  /**
   * Sauvegarde un article via l'API
   */
  async saveArticle(article: Article): Promise<Article> {
    const method = article.id ? 'PUT' : 'POST';
    
    // Utiliser directement l'URL de l'API
    const url = article.id 
      ? `${API_BASE_URL}/articles/${article.id}` 
      : `${API_BASE_URL}/articles`;
    
    
    // Sauvegarder le prix original calculé
    const prixOriginal = article.prixUnitaire;
    const prixCalculeOriginal = article.prixCalcule;
    
    
    const response = await fetch(url, {
      method,
      headers: this.createHeaders(),
      body: JSON.stringify(article),
    });
    
    if (!response.ok) {
      throw new Error(`Erreur lors de la sauvegarde de l'article`);
    }
    
    let savedArticle = await response.json();
    
    // S'assurer que le prix original est préservé
    savedArticle.prixUnitaire = prixOriginal;
    savedArticle.prixCalcule = prixCalculeOriginal;
    
    
    return savedArticle;
  }

  /**
   * Récupère la liste des devis depuis l'API
   */
  async getDevis(): Promise<Devis[]> {
    const response = await fetch(`${API_BASE_URL}/devis`, {
      headers: this.createHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des devis');
    }
    
    return await response.json();
  }

  /**
   * Récupère un devis par son ID
   */
  async getDevisById(id: string): Promise<Devis> {
    const response = await fetch(`${API_BASE_URL}/devis/${id}`, {
      headers: this.createHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération du devis ${id}`);
    }
    
    return await response.json();
  }

  /**
   * Sauvegarde un devis via l'API
   */
  async saveDevis(devis: Devis): Promise<Devis> {
    const method = devis.id ? 'PUT' : 'POST';
    const url = devis.id 
      ? `${API_BASE_URL}/devis/${devis.id}` 
      : `${API_BASE_URL}/devis`;
    
    const response = await fetch(url, {
      method,
      headers: this.createHeaders(),
      body: JSON.stringify(devis),
    });
    
    if (!response.ok) {
      throw new Error(`Erreur lors de la sauvegarde du devis`);
    }
    
    return await response.json();
  }

  /**
   * Génère le détail des pièces pour un devis sans calepinage
   */
  async genererDetailPieces(articles: Article[], matieres: Matiere[]): Promise<any> {
    try {
      
      const response = await fetch(`${API_BASE_URL}/devis/pieces-detail`, {
        method: 'POST',
        headers: this.createHeaders(),
        body: JSON.stringify({
          articles,
          matieres
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Erreur lors de la génération du détail des pièces: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      return result;
    } catch (error) {
      console.error("❌ Erreur génération détail pièces:", error);
      throw error;
    }
  }

  /**
   * Calcule les estimations de longueurs pour un devis
   */
  async calculerEstimationsLongueurs(data: {
    articles: Article[];
    matieres: Matiere[];
    preferences?: {
      k?: number;
      orientation_imposee?: boolean;
      motifs?: string;
      marge_securite_m?: number;
      granularite_m?: number;
    };
  }): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/devis/estimations-longueurs`, {
        method: 'POST',
        headers: this.createHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`Erreur lors du calcul des estimations: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("❌ Erreur calcul estimations longueurs:", error);
      throw error;
    }
  }

  /**
   * Sauvegarde les préférences d'estimation et les groupes de matières dans un devis
   */
  async sauvegarderEstimationsDevis(devisId: string, data: {
    preferences_estimation?: {
      k: number;
      date_calcul: string;
    };
    groupesMatiere?: any[];
  }): Promise<any> {
    try {
      // Récupérer le devis actuel
      const devis = await this.getDevisById(devisId) as any;
      
      // Mettre à jour devisOptimise avec les nouvelles données
      const devisOptimise = devis.devisOptimise || {};
      
      if (data.preferences_estimation) {
        devisOptimise.preferences_estimation = data.preferences_estimation;
      }
      
      if (data.groupesMatiere) {
        devisOptimise.groupesMatiere = data.groupesMatiere;
      }
      
      // Sauvegarder le devis mis à jour
      const updatedDevis = {
        ...devis,
        devisOptimise
      };
      
      return await this.saveDevis(updatedDevis);
    } catch (error) {
      console.error("❌ Erreur sauvegarde estimations devis:", error);
      throw error;
    }
  }
}

// Exporter une instance unique du service
export const apiService = new ApiService();










